"use client"

import type { ReactNode } from "react"

import { feedContinuationHref } from "@/features/feed/continuation"
import type { FeedPageType, FeedResponse } from "@/features/feed/contract"
import { FeedExperimentTracker } from "@/features/feed/FeedExperimentTracker"
import { FeedMoreLink } from "@/features/feed/FeedMoreLink"
import { FeedScroller } from "@/features/feed/FeedScroller"
import { useRenderedViewer } from "@/features/feed/viewer-feed"
import { ANONYMOUS_VIEWER } from "@/platform/session/viewer"

interface FeedViewerProps {
  feed: FeedResponse
  pageType: FeedPageType
  countryCode: string
  startIndex: number
  renderedFor: string | undefined
  personalize: boolean
  paging: boolean
  children: ReactNode
}

export const FeedViewer = ({ feed, pageType, countryCode, startIndex, renderedFor, personalize, paging, children }: FeedViewerProps) => {
  const { current, hidden } = useRenderedViewer(renderedFor, personalize)
  const viewer = renderedFor ?? ANONYMOUS_VIEWER
  const nextCursor = paging && feed.paging.hasMore ? feed.paging.nextCursor : null
  const nextIndex = startIndex + feed.sections.length

  return (
    <>
      {current && feed.experiment && <FeedExperimentTracker experiment={feed.experiment} />}
      {!hidden && children}
      {nextCursor &&
        (current ? (
          <FeedScroller
            key={viewer}
            pageType={pageType}
            handle={feed.page.handle}
            page={feed.page}
            countryCode={countryCode}
            initialCursor={nextCursor}
            startIndex={nextIndex}
            previousSection={feed.sections.at(-1)}
            viewer={viewer}
          />
        ) : (
          !hidden && <FeedMoreLink href={feedContinuationHref({ cursor: nextCursor, startIndex: nextIndex })} />
        ))}
    </>
  )
}
